<template>
  <div class="privileges-list">
    <!-- TODO: заменить модуль permission на privileges  -->
    <HeadSection title='Privileges' permissionOfSection="privileges">
      <template #actions>
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            style="font-size: 16px; font-weight: 300; border-radius: 2px;"
            @click="createPrivilege"
          >
            <a-icon type="plus" /> Create new
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="privileges">
      <div class="d-flex align-items-center mb-4">
        <AppSelect
          v-model="filters.applicationId"
          :apps="apps"
          :disabled="loading"
          class="mr-3"
          style="width: 220px;"
          @change="listUpdate"
        />
        <LocalizationsSelect
          v-model="filters.localizationId"
          :localizations="languagesOptions"
          :disabled="loading"
          class="mr-3"
          @change="listUpdate"
        />
        <ActiveStatus
          v-model="filters.active"
          :disabled="loading"
          hide-title
          style="margin-top: auto;"
          @change="listUpdate"
        />
        <div class="d-flex align-items-end justify-content-between ml-auto">
          <div>
            <span class="font-weight-bold">Search by</span>
            <a-select v-model="filters.selectedSearch" size="large" style="width: 140px">
              <a-select-option v-for="option in searchOptions" :key="option.value" :value="option.value">
                {{ option.title }}
              </a-select-option>
            </a-select>
          </div>
          <a-input-search size="large" v-model="filters.search" placeholder="Input search text" enter-button
            @change="debouncedListUpdate" :disabled="loading" class="" allow-clear />
        </div>
      </div>
      <div class="privilege-content" :class="(privileges.length <= 3 && !loading) && 'force-grid'">
        <div class="grid">
          <draggable
            v-if="!loading"
            v-model="privileges"
            v-bind="dragOptions"
            @start="drag = true"
            @end="sortStories()"
            tag="div"
          >
            <transition-group>
              <PrivilegeCard
                v-for="(privilege) in privileges"
                :key="privilege.uuid"
                :privilegeInfo="privilege"
                :editPermission="editPermission"
                :languages="languages"
                @openFullInfo="openPrivilegeModal(privilege)"
                @openEdit="openPrivilegeEdit(privilege.uuid)"
                @activeSwitch="privilegeActiveSwitch"
              />
            </transition-group>
          </draggable>
          <div v-else>
            <span>
              <PrivilegeCardSkeleton v-for="n in 10" :key="n" />
            </span>
          </div>
        </div>
        <a-modal
          v-model="isPrivilegeModalOpen"
          :footer="null"
          width="750px"
          :destroyOnClose="true"
          @cancel="closePrivilegeModal"
          @ok="closePrivilegeModal"
        >
          <template #title>
            <div>
              <div style="display: flex; align-items: center; gap: 1rem;">
                <span style="display: block;">
                  Privilege info
                </span>
                <a-switch
                  v-if="privilegeObject"
                  :default-checked="Boolean(privilegeObject.is_active)"
                  @change="(val) => privilegeActiveSwitch(val, privilegeObject.uuid)"
                  :disabled="!editPermission"
                />
              </div>
              <span class="modal-uuid">
                UUID: {{ privilegeUUID }}
              </span>
            </div>
          </template>
          <PrivilegeInfoModal
            :privilegeInfo="privilegeObject"
            :languages="languages"
            @close="closePrivilegeModal"
          />
        </a-modal>
      </div>
    </MainContainer>
  </div>
</template>
<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import AppSelect from '@/components/custom/filters/appSelect.vue'
import LocalizationsSelect from '@/components/custom/filters/localizationsSelect.vue'
import ActiveStatus from '@/components/custom/filters/activeStatus.vue'

import PrivilegeCard from '@/views/apps/privileges/components/privilegeCard.vue'
import PrivilegeCardSkeleton from '@/views/apps/privileges/components/privilegeCardSkeleton.vue'
import PrivilegeInfoModal from '@/views/apps/privileges/components/privilegeInfoModal.vue'

import draggable from 'vuedraggable'
import debounce from 'lodash.debounce'
import { mapState } from 'vuex'

import accessMix from '@/services/accessmix.js'
import apiClient from '@/services/axios'
import PrivilegeService from '@/services/api/apiPrivilegeService'

export default {
  components: {
    HeadSection,
    MainContainer,
    AppSelect,
    LocalizationsSelect,
    ActiveStatus,
    draggable,
    PrivilegeCard,
    PrivilegeCardSkeleton,
    PrivilegeInfoModal,
  },
  mixins: [accessMix],
  data() {
    return {
      privilegeLoading: false,
      appsLoading: false,
      languagesLoading: false,
      apps: undefined,
      languages: [],
      searchOptions: [
        { title: 'UUID', value: 'uuid' },
        { title: 'Name', value: 'name' },
      ],
      filters: {
        applicationId: undefined,
        localizationId: undefined,
        active: true,
        selectedSearch: 'uuid',
        search: '',
      },
      pagination: {
        current: 1,
        results: 15,
        total: 0,
      },
      drag: false,
      privileges: [],
      isPrivilegeModalOpen: false,
      privilegeObject: undefined,
    }
  },
  computed: {
    ...mapState(['user']),
    loading() {
      return this.privilegeLoading || this.languagesLoading || this.appsLoading
    },
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    languagesOptions() {
      return this.languages.map(function (row) {
        return { value: row.id, label: row.desc }
      })
    },
    privilegeUUID() {
      return this.privilegeObject ? this.privilegeObject.uuid : ''
    },
    permissionCreate() {
      return this.checkPermissions(this.user.permissions, 'privileges', 'create')
    },
    editPermission() {
      return this.checkPermissions(this.user.permissions, 'privileges', 'edit')
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      }
    },
  },
  methods: {
    createPrivilege() {
      this.$router.push('/privileges/create')
    },
    async fetchPrivileges() {
      const { active, localizationId, applicationId, selectedSearch, search } = this.filters

      const searchParams = new URLSearchParams()

      searchParams.append('is_active', active)

      searchParams.append('results', 250)

      if (typeof localizationId !== 'undefined') {
        searchParams.append('lid', localizationId)
      }

      if (typeof applicationId !== 'undefined') {
        searchParams.append('app_id', applicationId)
      }

      if (search?.length) {
        selectedSearch === 'uuid' ? searchParams.append('uuid', search) : searchParams.append('name', search)
      }

      const response = await apiClient.get(`admin/privilege?${searchParams}`)

      if (response?.data?.data) {
        this.privileges = response.data.data.filter(p => p.localizations.length > 0).filter(p => p.applications.length > 0)
      }
    },
    async listUpdate() {
      try {
        this.privilegeLoading = true
        await this.fetchPrivileges()
      } catch (e) {
        console.error(e)
      } finally {
        this.privilegeLoading = false
      }
    },
    debouncedListUpdate: debounce(function() {
      this.listUpdate()
    }, 1000),
    async fetchApplications() {
      this.appsLoading = true
      try {
        const url = '/admin/applications'
        const response = await apiClient.get(url)
        if (response.data.data) {
          this.apps = response.data.data
        }
      } finally {
        this.appsLoading = false
      }
    },
    async fetchLocalizations() {
      this.languagesLoading = true
      const url = '/admin/localizations'
      try {
        const response = await apiClient.get(url)
        this.languages = response.data.data
      } catch (error) {
        console.log('Error while trying to get locales', error)
      } finally {
        this.languagesLoading = false
      }
    },
    openPrivilegeEdit(id) {
      this.$router.push(`/privileges/edit/${id}`)
    },
    openPrivilegeModal(privilegeObject) {
      this.privilegeObject = privilegeObject
      this.isPrivilegeModalOpen = true
    },
    closePrivilegeModal() {
      this.isPrivilegeModalOpen = false
    },
    async privilegeActiveSwitch(checked, uuid) {
      if (!uuid) {
        return
      }

      try {
        const payload = { is_active: checked }

        const response = await PrivilegeService.changePrivilegeStatus(uuid, payload)

        if (response?.data?.error) {
          const errorMessage =
          response.data.error?.message ||
            'Something went wrong while updating privilege activity'
          this.$notification.error({
            message: errorMessage,
          })
          this.loading = false
          return
        } else {
          const p = this.privileges?.find(p => p.uuid === uuid)
          this.$notification.success({
            message: 'Active status changed',
            description: `${p?.name} status has been changed`,
          })

          this.listUpdate()
        }
      } catch (e) {
        const errorMessage =
          e.response?.data?.error?.message ||
          'Something went wrong while updating privilege activity'

        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    setFiltersFromQuery() {
      this.filters.applicationId = this.$route.query.applicationId
        ? Number(this.$route.query.applicationId)
        : (this.defaultApp ? this.defaultApp.application_id : undefined)

      this.filters.localizationId = this.$route.query.localizationId ? Number(this.$route.query.localizationId) : undefined

      this.filters.active = this.$route.query.active !== undefined ? this.$route.query.active === 'true' : true

      this.filters.search = this.$route.query.search || undefined
      this.filters.searchField = this.$route.query.searchField || undefined
    },
    async sortStories() {
      const payload = this.privileges?.map((p, index, arr) => {
        return {
          id: p.id,
          sort: arr.length - index,
        }
      })

      if (!payload?.length) {
        return
      }

      try {
        await apiClient.patch('/admin/privilege/sort/', payload)
        this.$notification.success({
          message: 'Sucessfully sorted',
        })
      } catch (error) {
        this.$notification.error({
          message: 'Error while sorting',
        })
      }
      this.drag = false
    },
  },
  async created() {
    await this.fetchApplications()
    await this.fetchLocalizations()
    await this.listUpdate()
    this.setFiltersFromQuery()
  },
  watch: {
    '$route.query': {
      deep: true,
      immediate: true,
      handler() {
        this.setFiltersFromQuery()
      },
    },
    filters: {
      handler(newFilters) {
        const queryObj = JSON.parse(JSON.stringify(this.$route.query))

        for (const key in queryObj) {
          let val = queryObj[key]

          if (val === 'true') val = true
          else if (val === 'false') val = false

          queryObj[key] = val
        }

        const filteredQuery = Object.fromEntries(
          Object.entries(newFilters).filter(([_, value]) => value !== undefined && value !== null && value !== ''),
        )

        if (JSON.stringify(queryObj) !== JSON.stringify(filteredQuery)) {
          this.$router.push({ query: filteredQuery })
        }
      },
      deep: true,
    },
  },
}
</script>
